import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getPublicProfile, getFollowCounts, isFollowing, follow, unfollow } from "@/lib/social";
import { listHostedEvents, listJoinedEvents } from "@/lib/events";
import { useAuth } from "@/context/AuthContext";
import { fmtError } from "@/lib/supabase";
import EventCard from "@/components/EventCard";
import Avatar from "@/components/Avatar";
import ReportButton from "@/components/ReportButton";
import {
  ArrowLeft, MessageCircle, Instagram, Ghost, MessagesSquare,
  UserPlus, UserMinus, MapPin, BadgeCheck, CalendarHeart, CalendarClock, CalendarCheck,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "upcoming", label: "Upcoming",   icon: CalendarClock },
  { key: "hosted",   label: "Hosted",     icon: CalendarHeart },
  { key: "past",     label: "Past",       icon: CalendarCheck },
];

export default function PublicProfilePage() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [counts, setCounts] = useState({ followers: 0, following: 0 });
  const [followingState, setFollowingState] = useState(false);
  const [hosted, setHosted] = useState([]);
  const [joined, setJoined] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");

  useEffect(() => {
    if (!handle) return;
    let alive = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      const p = await getPublicProfile(handle).catch(() => null);
      if (!alive) return;
      if (!p) { setNotFound(true); setLoading(false); return; }
      setProfile(p);

      const [c, f, hostedEvents, joinedEvents] = await Promise.all([
        getFollowCounts(p.id),
        user?.id ? isFollowing(user.id, p.id) : Promise.resolve(false),
        listHostedEvents(p.id).catch(() => []),
        listJoinedEvents(p.id).catch(() => []),
      ]);
      if (!alive) return;
      setCounts(c);
      setFollowingState(f);
      setHosted(hostedEvents);
      setJoined(joinedEvents);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [handle, user?.id]);

  const now = useMemo(() => new Date(), []);

  const upcoming = useMemo(() => {
    const events = joined.filter((e) => e.my_status === "approved" && new Date(e.date_time) >= now);
    return events.sort((a, b) => new Date(a.date_time) - new Date(b.date_time));
  }, [joined, now]);

  const past = useMemo(() => {
    const merged = [
      ...joined.filter((e) => e.my_status === "approved").map((e) => ({ ...e, _src: "joined" })),
      ...hosted.map((e) => ({ ...e, _src: "hosted" })),
    ];
    // dedupe by id
    const seen = new Set();
    const out = [];
    for (const e of merged) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      if (new Date(e.date_time) < now) out.push(e);
    }
    return out.sort((a, b) => new Date(b.date_time) - new Date(a.date_time));
  }, [hosted, joined, now]);

  const hostedUpcoming = useMemo(() => {
    return hosted.filter((e) => new Date(e.date_time) >= now)
      .sort((a, b) => new Date(a.date_time) - new Date(b.date_time));
  }, [hosted, now]);

  if (loading && !profile) {
    return (
      <div className="bg-paper min-h-[60vh] flex items-center justify-center px-6">
        <div className="flex flex-col items-center gap-4">
          <div className="w-32 h-32 border border-ink/15 bg-[#111] animate-pulse" />
          <div className="w-48 h-6 bg-[#111] border border-ink/15 animate-pulse" />
          <div className="w-32 h-4 bg-[#111] border border-ink/15 animate-pulse" />
        </div>
      </div>
    );
  }
  if (notFound || !profile) {
    return (
      <div className="bg-paper min-h-[60vh] flex items-center justify-center px-6 text-center">
        <div>
          <p className="font-display text-3xl text-ink">Profile not found.</p>
          <Link to="/feed" className="inline-block mt-6 text-xs uppercase tracking-widest text-ink hover:text-ink">← Back to feed</Link>
        </div>
      </div>
    );
  }

  const isMe = user?.id === profile.id;
  const displayName = profile.full_name || profile.display_name || profile.username;

  const onToggleFollow = async () => {
    if (!user) { navigate(`/login?next=/u/${profile.username || profile.id}`); return; }
    setBusy(true);
    try {
      if (followingState) {
        await unfollow(user.id, profile.id);
        setFollowingState(false);
        setCounts((c) => ({ ...c, followers: Math.max(0, c.followers - 1) }));
        toast.success("Unfollowed.");
      } else {
        await follow(user.id, profile.id);
        setFollowingState(true);
        setCounts((c) => ({ ...c, followers: c.followers + 1 }));
        toast.success("Following.");
      }
    } catch (e) { toast.error(fmtError(e)); }
    finally { setBusy(false); }
  };

  const onMessage = () => {
    if (!user) { navigate(`/login?next=/messages/${profile.id}`); return; }
    navigate(`/messages/${profile.id}`);
  };

  const visible =
    tab === "upcoming" ? upcoming
    : tab === "hosted"  ? hostedUpcoming.concat(hosted.filter((e) => new Date(e.date_time) < now).sort((a,b)=>new Date(b.date_time)-new Date(a.date_time)))
    : past;

  return (
    <div className="bg-paper min-h-[calc(100vh-4rem)]">
      {/* HERO */}
      <div className="relative border-b border-ink/15 overflow-hidden">
        {/* glow background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-neon/10 blur-[120px]" />
          <div className="absolute -bottom-32 right-0 w-80 h-80 bg-neon/5 blur-[140px]" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-12">
          <button onClick={() => navigate(-1)} data-testid="profile-back"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink/60 hover:text-ink transition-colors mb-8">
            <ArrowLeft size={14} strokeWidth={2} /> Back
          </button>

          <div className="flex flex-col md:flex-row gap-8 md:gap-10 items-start md:items-end">
            <Avatar
              src={profile.avatar_url}
              name={displayName}
              size="2xl"
              glow
              testid="profile-avatar"
            />

            <div className="flex-1 flex flex-col gap-4 w-full">
              <div className="flex flex-wrap items-center gap-3">
                <h1 data-testid="profile-name" className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[0.95]">
                  {displayName}
                </h1>
                {profile.is_verified_admin && (
                  <span data-testid="verified-badge" className="inline-flex items-center gap-1.5 bg-lime text-ink border-[2.5px] border-ink rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest shadow-pop-sm">
                    <BadgeCheck size={12} strokeWidth={2.5} /> Verified
                  </span>
                )}
              </div>

              {profile.username && (
                <span data-testid="profile-username" className="text-ink/60 text-sm">
                  @{profile.username}
                </span>
              )}

              {profile.bio && (
                <p data-testid="profile-bio" className="text-ink/80 leading-relaxed max-w-prose">
                  {profile.bio}
                </p>
              )}

              {profile.city && (
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-ink/55">
                  <MapPin size={12} strokeWidth={1.8} className="text-ink" /> {profile.city}
                </span>
              )}

              <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm pt-2">
                <Stat n={counts.followers}  label="followers" testid="stat-followers" />
                <Stat n={counts.following}  label="following" testid="stat-following" />
                <Stat n={hosted.length}     label="hosting"   testid="stat-hosting"   />
                <Stat n={joined.filter(e=>e.my_status==='approved').length} label="joined" testid="stat-joined" />
              </div>

              {/* Social links */}
              {(profile.whatsapp || profile.instagram || profile.snapchat) && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {profile.whatsapp && (
                    <SocialLink
                      icon={MessageCircle}
                      href={`https://wa.me/${profile.whatsapp.replace(/[^0-9+]/g, "")}`}
                      label="WhatsApp"
                      testid="social-whatsapp"
                    />
                  )}
                  {profile.instagram && (
                    <SocialLink
                      icon={Instagram}
                      href={`https://instagram.com/${profile.instagram.replace(/^@/, "")}`}
                      label={`@${profile.instagram.replace(/^@/, "")}`}
                      testid="social-instagram"
                    />
                  )}
                  {profile.snapchat && (
                    <SocialLink
                      icon={Ghost}
                      href={`https://snapchat.com/add/${profile.snapchat.replace(/^@/, "")}`}
                      label={`@${profile.snapchat.replace(/^@/, "")}`}
                      testid="social-snapchat"
                    />
                  )}
                </div>
              )}

              {/* CTAs */}
              <div className="flex flex-wrap gap-3 mt-4">
                {isMe ? (
                  <Link to="/profile" data-testid="profile-edit-link"
                    className="inline-flex items-center gap-2 bg-neon text-ink hover:bg-paper px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors">
                    Edit your profile
                  </Link>
                ) : (
                  <>
                    <button data-testid="profile-follow-btn" onClick={onToggleFollow} disabled={busy}
                      className={`inline-flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-60 ${
                        followingState
                          ? "border border-ink text-ink hover:bg-destructive hover:text-ink hover:border-destructive"
                          : "bg-neon text-ink hover:bg-paper"
                      }`}>
                      {followingState
                        ? <><UserMinus size={14} strokeWidth={2.5} /> Following</>
                        : <><UserPlus  size={14} strokeWidth={2.5} /> Follow</>}
                    </button>
                    <button data-testid="profile-message-btn" onClick={onMessage}
                      className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-paper hover:text-ink px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors">
                      <MessagesSquare size={14} strokeWidth={2.5} /> Message
                    </button>
                    <ReportButton targetType="profile" targetId={profile.id} testid="profile-report-btn" />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TABS + LIST */}
      <div className="max-w-5xl mx-auto px-6 md:px-10 pt-10 pb-20">
        <div data-testid="profile-tabs" className="flex flex-wrap gap-2 border-b border-ink/15 mb-8">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-widest border-b-2 -mb-px transition-colors ${
                  active
                    ? "text-ink border-neon"
                    : "text-ink/55 hover:text-ink border-transparent"
                }`}
              >
                <Icon size={13} strokeWidth={2} /> {t.label}
              </button>
            );
          })}
        </div>

        {visible.length === 0 ? (
          <div className="border border-dashed border-ink/15 p-12 text-center text-ink/60 text-sm">
            Nothing here yet.
          </div>
        ) : (
          <div data-testid="profile-events-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ n, label, testid }) {
  return (
    <span className="inline-flex items-baseline gap-1.5" data-testid={testid}>
      <strong className="font-display text-ink text-2xl md:text-3xl">{n}</strong>
      <span className="text-xs uppercase tracking-widest text-ink/55">{label}</span>
    </span>
  );
}

function SocialLink({ icon: Icon, href, label, testid }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
      className="inline-flex items-center gap-2 border border-ink/15 hover:border-neon hover:text-ink text-ink px-3 py-2 text-xs uppercase tracking-widest transition-colors">
      <Icon size={12} strokeWidth={1.8} /> {label}
    </a>
  );
}
